import request from '@apps-common/js-client/api/request';
import {PDA_ADMIN_V1_BASE_PATH} from '@account/config';
import {useMutation, useQuery, useQueryClient} from '@tanstack/react-query';

const queryKey = 'archiveControl';

const transformPolicy = policy => {
  const newPolicy = {...policy};
  if (!newPolicy?.body?.collection_ref) {
    newPolicy.body.collection_ref = policy?.body?.aois?.[0]?.geometry.content;
  }
  if (!newPolicy?.body?.start_date) {
    newPolicy.body.start_date = policy?.body?.delay_ramp_effective_date;
  }

  newPolicy.collectionId = newPolicy.body?.collection_ref?.split('/').at(2);

  return newPolicy;
};

const getPolicies = params =>
  request
    .get(`${PDA_ADMIN_V1_BASE_PATH}/archive-control/policies`, {
      params,
    })
    .then(res => res.data);

const getPolicy = policyId =>
  request
    .get(`${PDA_ADMIN_V1_BASE_PATH}/archive-control/policies/${policyId}`)
    .then(res => res.data);

const getPolicyAuditLog = (policyId, params) =>
  request
    .get(
      `${PDA_ADMIN_V1_BASE_PATH}/audit-logs/archive-control-policies/${policyId}`,
      {
        params: {
          offset: 0,
          limit: 5,
          sort: '-created_at',
          ...params,
        },
      }
    )
    .then(res => res.data);

const togglePolicy = ({policyId, enable}) =>
  request
    .post(
      `${PDA_ADMIN_V1_BASE_PATH}/archive-control/policies/${policyId}/${enable ? 'enable' : 'disable'}`
    )
    .then(res => res.data);

const createPolicy = policy =>
  request
    .post(`${PDA_ADMIN_V1_BASE_PATH}/archive-control/policies`, policy)
    .then(res => res.data);

const updatePolicy = policy =>
  request.patch(
    `${PDA_ADMIN_V1_BASE_PATH}/archive-control/policies/${policy.id}`,
    policy
  );

export const useGetAccessRestrictions = queryParams =>
  useQuery({
    queryKey: [queryKey, 'list', queryParams],
    queryFn: () => getPolicies(queryParams),
    select: data => data.map(transformPolicy),
  });

export const useGetPolicy = ({policyId}, queryOptions = {}) =>
  useQuery({
    queryKey: [queryKey, 'details', policyId],
    queryFn: () => getPolicy(policyId),
    select: data => transformPolicy(data),
    ...queryOptions,
  });

export const useGetPolicyAuditLog = (
  {policyId, params = {}},
  queryOptions = {}
) =>
  useQuery({
    queryKey: [queryKey, 'auditLog', policyId, params],
    queryFn: () => getPolicyAuditLog(policyId, params),
    ...queryOptions,
  });

export const useTogglePolicy = queryOptions => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: togglePolicy,
    onSettled: () => {
      queryClient.invalidateQueries({
        queryKey: [queryKey],
      });
    },
    ...queryOptions,
  });
};

export const useCreatePolicy = queryOptions => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: createPolicy,
    onSettled: () => {
      queryClient.invalidateQueries({
        queryKey: [queryKey],
      });
    },
    ...queryOptions,
  });
};

export const useUpdatePolicy = queryOptions => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: updatePolicy,
    onSettled: () => {
      queryClient.invalidateQueries({
        queryKey: [queryKey],
      });
    },
    ...queryOptions,
  });
};
